import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ClipboardList,
  LogOut,
  Menu,
  User as UserIcon,
} from 'lucide-react';
import { useCurrentUser, useLogout } from '../../hooks/useAuth';
import { LiveStatusBadge } from './LiveStatusBadge';
import { MobilisationSwitcher } from './MobilisationSwitcher';
import { MobileNavDrawer } from './MobileNavDrawer';
import { ThemeToggle } from './ThemeToggle';
import { FontSizeControl } from './FontSizeControl';
import { hasPrimaryNav } from './navConfig';

/**
 * Slim top chrome above the page content. On `lg+` the sidebar carries
 * the brand and primary navigation, so this strip is reduced to the
 * mobilisation switcher + theme toggle + user actions. Below `lg` it
 * gains a hamburger (toggling the MobileNavDrawer) and a compact
 * wordmark.
 */
export function TopBar() {
  const { data: user, isLoading } = useCurrentUser();
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isLoading || !user) return null;

  // When the user has no sidebar (inductees), the rail is hidden, so the brand
  // has to live here on every breakpoint instead of only below `lg`.
  const noSidebar = !hasPrimaryNav(user);

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line/10 bg-surface px-3 shadow-token-sm sm:px-4 print:hidden">
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-9 w-9 min-h-touch items-center justify-center rounded-token-sm border border-line-strong/15 bg-surface text-fg-muted transition-colors hover:bg-surface-2 lg:hidden"
        >
          <Menu className="h-4 w-4" />
        </button>

        <Link
          to="/"
          className={[
            'flex shrink-0 items-center gap-2 rounded-md text-fg transition-colors hover:text-accent',
            noSidebar ? '' : 'lg:hidden',
          ].join(' ')}
        >
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-token-sm text-fg-on-accent shadow-token-accent ring-1 ring-accent/30"
            style={{
              backgroundImage:
                'linear-gradient(135deg, rgb(var(--accent)) 0%, #ec4899 100%)',
            }}
          >
            <ClipboardList className="h-4 w-4" />
          </span>
          {noSidebar ? (
            <span className="text-sm font-semibold tracking-tight text-fg">Onboarding</span>
          ) : null}
        </Link>

        <div className="ml-auto flex items-center gap-2 text-sm">
          <FontSizeControl />
          <ThemeToggle />
          <LiveStatusBadge />
          <MobilisationSwitcher />
          {user.person_id ? (
            <NavLink
              to="/me"
              title="My record"
              className={({ isActive }) =>
                [
                  'hidden h-9 items-center gap-1.5 rounded-token-sm px-2.5 text-xs font-medium transition-colors md:inline-flex',
                  isActive
                    ? 'bg-accent text-fg-on-accent shadow-token-sm'
                    : 'border border-line-strong/15 bg-surface text-fg-muted hover:bg-surface-2',
                ].join(' ')
              }
            >
              <UserIcon className="h-3.5 w-3.5" />
              <span>My record</span>
            </NavLink>
          ) : null}
          <span
            className="hidden h-9 items-center rounded-token-sm bg-surface-2 px-2.5 text-xs font-medium text-fg-muted md:inline-flex"
            title={user.email}
          >
            {user.display_name}
          </span>
          <button
            type="button"
            onClick={() => logout.mutate()}
            aria-label="Sign out"
            title="Sign out"
            className="inline-flex h-9 min-h-touch items-center gap-1.5 rounded-token-sm border border-line-strong/15 bg-surface px-2.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-2"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <MobileNavDrawer
        user={user}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onLogout={() => logout.mutate()}
      />
    </>
  );
}
